import _ from 'lodash';
import moment from 'moment';
import 'moment/locale/he';
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import createAppTheme from '@/utils/MuiArbitripTheme';
import ProfileStore from '../../stores/ProfileStore';
import AppGridStore from '../../stores/AppGridStore';
import SearchStore from '../../stores/SearchStore';
import AppGridActions from '../../actions/AppGridActions';
import TopBar from './bars/top/TopBar.react';
import FooterBar from './bars/footer/FooterBar';
import LoadingComponent from './LoadingComponent.react';
import LogActions from '../../actions/LogActions';
import MaterialDesignDialog from '../general/MaterialDesignDialog.react';
import SearchTermsParser from '../../utils/SearchTermsParser';
import RouterWrapper from '../../utils/RouterWrapper';
import SearchActions from '../../actions/SearchActions';
import { v4 as uuidV4 } from 'uuid';
import Analytics from 'arbitrip-common/client/analytics';
import Config from 'arbitrip-common/client/utils/Config';
import defaultThemeConfig from 'arbitrip-common/theme/colors/configs/default.json';
import {
    SUPPORTED_LANGUAGES,
    normalizeLanguage,
    isSupportedLanguage,
    resolveSupportedLanguage,
} from 'arbitrip-common/translation/const';
import { CLIENT_ROUTES as Routes } from 'arbitrip-common/general/constants';
import * as Sentry from '@sentry/react';
import { loadThemeConfig, setCssVariables, loadThemeFonts } from 'arbitrip-common/theme/colors/helpers';
import updateFavicon from 'arbitrip-common/client/utils/updateFavicon';
import Riskified from 'arbitrip-common/client/utils/Riskified';
import HotelActions from '../../actions/HotelActions';
import ThemeLoadingError from '../general/ThemeLoadingError.react';
import channel from '@/utils/BroadcastChannel';
import { isElalCompany, isIsracardCompany, isAirHaifaCompany } from '@/utils/isCompany';
import IsracardPromotionBar from './bars/top/IsracardPromotionBar.react';
import AirHaifaPromotionBar from './bars/top/AirHaifaPromotionBar.react';
import ElalAnonymousPromotionBar from './bars/top/ElalAnonymousPromotionBar.react';
import CookieConcentBanner from './CookieConcentBanner';
import {
    TAS_ID_KEY,
    setTasIdInSessionStorage,
    removeTasIdFromSessionStorage,
    getTasIdFromSessionStorage,
} from '@/utils/TasId';

let did_boot = false;
let did_parse = false;
let user_currency = '';

const ROUTES_WITH_FOOTER = [Routes.SEARCH, Routes.FAQ, Routes.ABOUT_US, Routes.CONTACT_US];
import { getCookiesLng, hasUserApprovedCookies } from '../../utils/cookies';

class AppGrid extends Component {
    constructor(props) {
        super(props);
        this.state = {
            ...getAppGridState(),
            themeConfig: null,
            materialUITheme: createAppTheme(defaultThemeConfig),
            tasId: getTasIdFromSessionStorage() || null,
            themeLoadError: null,
        };

        this._onChange = this._onChange.bind(this);
        this.changeLanguage = this.changeLanguage.bind(this);
        this.updateLanguageOnDB = this.updateLanguageOnDB.bind(this);
        this.applyPreferredLanguage = this.applyPreferredLanguage.bind(this);
    }

    componentDidMount() {
        Analytics.startGtmSession();
        Riskified.loadBeacon();

        window.changeLanguage = (lang) => this.changeLanguage(lang);

        moment.locale(SUPPORTED_LANGUAGES.EN);

        if (this.isSpecificPage(Routes.SEARCH)) {
            this.setState({ slowAnimation: true });
        }

        ProfileStore.addChangeListener(this._onChange);
        SearchStore.addChangeListener(this._onChange);
        AppGridStore.addChangeListener(this._onChange);

        window.addEventListener('storage', this.handleStorageChange);

        // Handle messages from other tabs to close tasId session
        channel.onmessage = (event) => {
            if (event.data === this.state.tasId) {
                removeTasIdFromSessionStorage();
                this.setState({ tasId: null });
            }
        };
    }

    componentWillUnmount() {
        ProfileStore.removeChangeListener(this._onChange);
        SearchStore.removeChangeListener(this._onChange);
        AppGridStore.removeChangeListener(this._onChange);

        window.removeEventListener('storage', this.handleStorageChange);
        channel.close();

        this.setState({ sessionTimeoutModalIsOpen: false });
    }

    componentDidCatch(error, info) {
        if (Config.prod === true) {
            let search_params = SearchStore.toAjaxPayload();
            LogActions.logError({ error, context: { info, search_params } });
            setTimeout(() => {
                document.location.href = document.location.origin;
            }, 100);
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const { profile } = this.state;
        const { i18n } = this.props;

        // TODO: consider moving to OfflineDB boot instead of AppGrid (specifically, loading search session related stuff)
        if (!did_boot && this.state.has_contract_id) {
            setTimeout(() => {
                SearchActions.updateSearchContractById();
            }, 10);
        }
        if (!did_boot && profile.email && ProfileStore.isServerProfileLoaded() && AppGridStore.isIdle()) {
            try {
                const session_id = uuidV4();
                Analytics.actions.global_properties.setItem('session_id', session_id);

                this.loadAccessibilityWidget();

                (async () => {
                    Analytics.startSession(profile, { language: i18n.language });
                    Analytics.actions.entry_source.registerDefaultEntrySource();
                })();

                // Apply theme config with error handling
                this.applyThemeConfig(i18n.dir()).catch((error) => {
                    // Check if it's a domain-matched theme error
                    if (error.isDomainMatched || error.name === 'DomainMatchedThemeError') {
                        this.setState({ themeLoadError: error });
                    } else {
                        // Log other errors but don't block app
                        console.error('Theme loading error:', error);
                        Sentry.captureException(error);
                    }
                });
            } catch (err) {
                console.error(err);
                Sentry.captureException(err);
            }

            did_boot = true;
            this.setState({ did_boot });
        }
    }

    closeSessionTimeoutModal() {
        const profile = ProfileStore.getProfile();
        if (profile?.custom_logout_link) {
            document.location.href = profile.custom_logout_link;
        } else if (Config.htz) {
            document.location.href = Config.htz_link;
        } else {
            document.location.reload();
        }
    }

    isSpecificPage(page_route) {
        let path = this.props.location && this.props.location.pathname;
        let route = typeof path === 'string' ? path.substr(1) : '';
        return route.startsWith(page_route);
    }

    areSpecificPages(routes) {
        return routes.some((route) => this.isSpecificPage(route));
    }

    async applyThemeConfig(direction) {
        const companyId = _.get(this.state, 'profile.company.id');
        const themeConfig = await loadThemeConfig(companyId, Config.dev_mode);

        loadThemeFonts(themeConfig); // load custom fonts from theme config
        setCssVariables(themeConfig); // set css variables globally from the theme config
        const materialUITheme = createAppTheme(themeConfig, direction); // adjust material-ui theme from the theme config

        // Update favicon if ELAL theme
        if (companyId) {
            updateFavicon(companyId, false);
        }

        this.setState({ themeConfig, materialUITheme });
    }

    loadAccessibilityWidget() {
        if (!ProfileStore.shouldShowAccessibilityWidget()) {
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/gh/mickidum/acc_toolbar/acctoolbar/acctoolbar.min.js';
        script.async = true;
        script.onload = () => {
            window.micAccessTool = new window.MicAccessTool();
        };

        // append to top of body
        document.body.appendChild(script);
    }

    shouldDisplayCombtasBar() {
        const { profile } = this.state;
        const isCombtasEnabled = _.get(profile, 'company.settings.combtas_integration_enabled');
        const shouldDisplayOnCurrentPage = this.areSpecificPages([Routes.SEARCH, Routes.RESULTS, Routes.HOTEL]);
        return isCombtasEnabled && this.state.tasId && shouldDisplayOnCurrentPage;
    }

    shouldDisplayIsracardPromotionBar() {
        const { profile } = this.state;
        return isIsracardCompany() && !profile.isLoggedIn && this.areSpecificPages([Routes.SEARCH, Routes.RESULTS]);
    }

    shouldDisplayAirHaifaPromotionBar() {
        const { profile } = this.state;
        return isAirHaifaCompany() && !profile.isLoggedIn && this.areSpecificPages([Routes.SEARCH, Routes.RESULTS]);
    }

    shouldDisplayElalAnonymousPromotionBar() {
        const { profile } = this.state;
        const isAnonymous = profile.anonymous || !profile.isLoggedIn;
        return isElalCompany() && isAnonymous && this.areSpecificPages([Routes.SEARCH, Routes.RESULTS]);
    }

    getPageContainerPaddingTop() {
        let height = 75;
        if (this.shouldDisplayCombtasBar()) {
            height += 33;
        }
        return `${height}px`;
    }

    endCombtasSession() {
        try {
            // Notify other tabs to close the current tasId session
            channel.postMessage(this.state.tasId);
        } catch (error) {
            console.log(error);
        }

        removeTasIdFromSessionStorage();
        this.setState({ tasId: null });

        const url = new URL(window.location);
        url.searchParams.delete(TAS_ID_KEY);
        window.history.replaceState({}, document.title, url.toString());
    }

    handleStorageChange = (event) => {
        if (!getTasIdFromSessionStorage()) {
            this.setState({ tasId: null });
        }
    };

    updateLanguageOnDB(language) {
        AppGridActions.updateLanguageOnDB({ language });
    }

    changeLanguage(requestedLang) {
        const { i18n } = this.props;
        const lang = resolveSupportedLanguage(requestedLang);

        i18n.changeLanguage(lang);

        // Update document direction
        const newDirection = i18n.dir(lang);
        document.dir = newDirection;
        document.body.dir = newDirection;
        document.documentElement.setAttribute('lang', lang);

        // Create a new MUI theme with the updated direction
        const updatedTheme = createAppTheme(this.state.themeConfig ?? defaultThemeConfig, newDirection);
        this.setState({ materialUITheme: updatedTheme });

        // Change moment locale
        moment.locale(lang);

        // Save language preference
        localStorage.setItem('preferredLanguage', lang);

        if (window.OneTrust && !hasUserApprovedCookies()) {
            window.OneTrust.changeLanguage(getCookiesLng(newDirection));
        }
        
        // Update the 'lang' URL parameter if it currently exists
        const url = new URL(window.location);
        if (url.searchParams.has('lang')) {
            url.searchParams.set('lang', lang);
            window.history.replaceState({}, document.title, url.toString());
        }
    }

    applyPreferredLanguage(profile) {
        const isHebrewOnly = _.get(profile, 'company.partners_ui.force_hebrew_only', false);
        if (isHebrewOnly) {
            this.changeLanguage(SUPPORTED_LANGUAGES.HE);
            return;
        }

        const urlLang = new URLSearchParams(window.location.search).get('lang')?.toLowerCase();
        const profileLang = _.get(profile, 'settings.language');
        const storedLang = localStorage.getItem('preferredLanguage');

        const lang = resolveSupportedLanguage(urlLang, storedLang, profileLang);

        if (this.props.i18n.language !== lang) {
            this.changeLanguage(lang);
        }

        localStorage.setItem('preferredLanguage', lang);

        if (isSupportedLanguage(urlLang) && lang !== normalizeLanguage(profileLang)) {
            AppGridActions.updateLanguageOnDB({ language: lang });
        }
    }

    render() {
        const { t } = this.props;

        // Show theme loading error page if domain-matched theme failed to load
        if (this.state.themeLoadError) {
            return <ThemeLoadingError />;
        }

        let appGridClass = 'appGrid';
        if (!this.state.did_boot) {
            appGridClass += ' fade-in';
            if (this.state.slowAnimation) {
                appGridClass += ' slow-animation';
            }
        }
        if (
            this.state.pending ||
            this.state.parsing ||
            !this.state.profile.hasOwnProperty('email') ||
            !this.state.themeConfig
        ) {
            return (
                <ThemeProvider theme={this.state.materialUITheme}>
                    <StyledEngineProvider injectFirst>
                        <LoadingComponent pending={this.state.pending && !this.isSpecificPage(Routes.HOTEL)} />
                    </StyledEngineProvider>
                </ThemeProvider>
            );
        } else {
            return (
                <ThemeProvider theme={this.state.materialUITheme}>
                    <StyledEngineProvider injectFirst>
                        <div className={appGridClass}>
                            <div className="gridRow">
                                <TopBar
                                    user={this.state.profile}
                                    company={this.state.profile.company}
                                    reservationPage={this.isSpecificPage(Routes.RESERVATION)}
                                    tasId={this.state.tasId}
                                    showCombtasBar={this.shouldDisplayCombtasBar()}
                                    endCombtasSession={() => this.endCombtasSession()}
                                    changeLanguage={this.changeLanguage}
                                    updateLanguageOnDB={this.updateLanguageOnDB}
                                />
                            </div>
                            <div
                                id="page-container"
                                className="gridRow"
                                style={{
                                    paddingTop: this.getPageContainerPaddingTop(),
                                }}
                            >
                                {this.shouldDisplayIsracardPromotionBar() && <IsracardPromotionBar />}
                                {this.shouldDisplayElalAnonymousPromotionBar() && <ElalAnonymousPromotionBar />}
                                {this.shouldDisplayAirHaifaPromotionBar() && <AirHaifaPromotionBar />}
                                {this.props.children}
                            </div>
                            {this.areSpecificPages(ROUTES_WITH_FOOTER) && (
                                <div className="gridRow">
                                    <FooterBar profile={this.state.profile} />
                                </div>
                            )}
                            <CookieConcentBanner profile={this.state.profile} />
                            <MaterialDesignDialog
                                open={this.state.sessionTimeoutModalIsOpen}
                                onClose={this.closeSessionTimeoutModal}
                                closeTimeoutInMilliseconds={150}
                                title={t('SessionHaveExpired')}
                                content={t('PleaseLoginAgain')}
                                actions={[
                                    {
                                        onClick: () => {
                                            Analytics.actions.sessions.sessionExpiredLoginClicked(this.state.profile);
                                            this.closeSessionTimeoutModal();
                                        },
                                        text: t('Login'),
                                    },
                                ]}
                            />
                        </div>
                    </StyledEngineProvider>
                </ThemeProvider>
            );
        }
    }

    _onChange() {
        let profile = ProfileStore.getProfile();

        if (!_.isEmpty(profile) && !did_parse) {
            did_parse = true;

            this.applyPreferredLanguage(profile);

            AppGridActions.setAppStatusAsParsing();
            SearchTermsParser.parseValidSearchTermsFromURL((search_terms) => {
                SearchActions.updateSearchTermsViaUrl(search_terms);
                let search_token = SearchStore.getToken();
                let hotel_page = this.isSpecificPage(Routes.HOTEL);
                if (search_terms.tas_id && _.get(profile, 'company.settings.combtas_integration_enabled')) {
                    setTasIdInSessionStorage(search_terms.tas_id);
                    this.setState({ tasId: search_terms.tas_id });
                }
                if (this.isSpecificPage(Routes.RESULTS) || (search_token && hotel_page)) {
                    if (
                        hotel_page &&
                        !_.get(search_terms, 'destination.place.place_id') &&
                        !_.get(search_terms, 'destination.place.hotel_id')
                    ) {
                        const hotel_id = parseHotelId();
                        if (hotel_id) {
                            _.set(search_terms, 'destination.place.hotel_id', hotel_id);
                        }
                    }
                    if (ProfileStore.isUseUserCurrency()) {
                        _.set(search_terms, 'user_currency', ProfileStore.getUserCurrency());
                    }
                    if (SearchTermsParser.validate(search_terms)) {
                        SearchActions.search(search_token, { forceCache: hotel_page });
                    } else {
                        AppGridActions.setAppStatusAsIdle();
                        RouterWrapper.goToSearchPage(this.props.navigate, search_terms);
                    }
                } else {
                    AppGridActions.setAppStatusAsIdle();
                }
            });
        } else if (
            ProfileStore.isUseUserCurrency() &&
            user_currency &&
            user_currency !== ProfileStore.getUserCurrency()
        ) {
            SearchTermsParser.parseValidSearchTermsFromURL((search_terms) => {
                let search_token = SearchStore.getToken();
                let hotel_page = this.isSpecificPage(Routes.HOTEL);
                if (this.isSpecificPage(Routes.RESULTS) || (search_token && hotel_page)) {
                    _.set(search_terms, 'user_currency', ProfileStore.getUserCurrency());

                    if (hotel_page) {
                        const hotel_id = parseHotelId();
                        if (hotel_id) {
                            _.set(search_terms, 'destination.place.hotel_id', hotel_id);
                            AppGridActions.setAppStatusAsIdle();
                            HotelActions.clearHotelDeals(hotel_id);
                            HotelActions.getHotelRecheckedDeals(hotel_id, search_token);
                        }
                    } else {
                        SearchActions.search(null, { forceCache: hotel_page });
                    }
                }
            });
        }
        user_currency = ProfileStore.getUserCurrency();
        this.setState(getAppGridState());
    }
}

function getAppGridState() {
    const _state = {
        profile: ProfileStore.getProfile(),
        sessionTimeoutModalIsOpen: !ProfileStore.isSessionActive(),
        idle: AppGridStore.isIdle(),
        pending: AppGridStore.isPending(),
        parsing: AppGridStore.isParsing(),
        did_boot,
        has_contract_id: !!SearchStore.getContractId(),
    };
    return _state;
}

function parseHotelId() {
    const split_path = new URL(window.location.href).pathname.split('/').filter(_.identity);

    if (split_path[0].toLowerCase() === 'hotel') {
        return split_path[2];
    }

    return null;
}

export default withTranslation()(AppGrid);
